import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import { client } from 'f';
import { useLocalStorage } from 'lib/hooks/useLocalStorage';

import { useModalStore } from '../../Modals';
import { useTrackClickEvent } from '../../analytics';
import { updateUrlWithParams, waitForRouteChange } from '../../utils';
import type { TopicsLaunchPadProps } from './TopicsLaunchPad.props';

type UseTopicsLaunchPadLogicProps = Omit<
  TopicsLaunchPadProps,
  'isOnResultsPage'
> & {
  refine: (topic: string) => void;
};

interface UseTopicsLaunchPadLogicReturn {
  handleOnSelect: (selectedTopic: string) => void;
}

export const useTopicsLaunchPadLogic = ({
  page,
  position,
  refine
}: UseTopicsLaunchPadLogicProps): UseTopicsLaunchPadLogicReturn => {
  const router = useRouter();
  const ISTALKSPAGE = router.pathname === '/talks';

  const { addSelectedTopic } = useModalStore();

  const { indexUiState, results } = useInstantSearch();
  const trackClick = useTrackClickEvent(
    results
      ? {
          index: results.index,
          nbHits: results.nbHits
        }
      : undefined
  );
  const [, setTopicLaunchPadContext] = useLocalStorage('topic_launchpad', '');

  const handleOnSelect = useCallback(
    async (selectedTopic: string) => {
      addSelectedTopic(selectedTopic);

      updateUrlWithParams(router, {
        'topics[0]': selectedTopic
      });

      if (page === 'Default') {
        setTopicLaunchPadContext(selectedTopic);

        trackClick({
          eventName: 'topic_launchpad_selection',
          additionalEvents: {
            topic_launchpad: selectedTopic,
            event_interaction_context:
              position === 'Top'
                ? 'topic_launchpad_header'
                : 'topic_launchpad_footer',
            topics: selectedTopic
          },
          waitForResultsVolume: true
        });
      }

      if (
        page === 'Results' ||
        (page === 'Default' && position === 'Bottom') ||
        page === 'Home'
      ) {
        if (client) window.scrollTo({ top: 0, behavior: 'smooth' });

        setTopicLaunchPadContext(selectedTopic);

        trackClick({
          eventName: 'topic_launchpad_selection',
          additionalEvents: {
            topic_launchpad: selectedTopic,
            event_interaction_context:
              page === 'Home'
                ? 'topic_launchpad_header'
                : 'topic_launchpad_footer'
          },
          waitForResultsVolume: true
        });
      }

      if (!ISTALKSPAGE) {
        await waitForRouteChange();
        refine(selectedTopic);
      }
    },
    [
      addSelectedTopic,
      page,
      position,
      ISTALKSPAGE,
      router,
      indexUiState.sortBy,
      setTopicLaunchPadContext,
      trackClick,
      refine
    ]
  );

  return { handleOnSelect };
};
